/**
 * "Try again" on "No interpreter is available." sends the same request again
 * with one tap (Robert, 2026-11-02, question 3): the same language, region,
 * call type, number and people. The client's last request is kept here when
 * requestInterpreter() sends it, and retryInterpreterRequest() (actions.ts)
 * sends a copy.
 *
 * The request belongs to the account that sent it (its owner: account id,
 * tenant, role and server). A retry for another owner is refused, so after a
 * sign-out, an account switch or a role switch on the same phone "Try again"
 * never sends the previous person's number, people or region. The request is
 * also forgotten on sign-out, on a match, on the person's own Cancel, and when
 * the "Try again" notice goes.
 *
 * Pure, with no imports, so its unit test needs no queue service.
 */
let last = null;
/** Keeps a copy of the request that was just sent, for its owner. No owner (signed out): nothing is kept. */
export function rememberInterpreterRequest(language, options, owner) {
    last = owner ? { language, options: { ...options }, owner } : null;
}
export function lastInterpreterRequest() {
    return last;
}
export function forgetInterpreterRequest() {
    last = null;
}
/**
 * The request to send for "Try again", or null when there is nothing to send:
 * no request was kept, a request is waiting now, or the signed-in owner is not
 * the owner of the kept request.
 */
export function retryRequestArgs(remembered, isRequestPending, currentOwner) {
    if (!remembered || isRequestPending || !currentOwner || remembered.owner !== currentOwner) {
        return null;
    }
    return [remembered.language, { ...remembered.options }];
}
/**
 * The owner of a request: the signed-in account id, tenant, role and server.
 * Null when no account is signed in.
 */
export function requestOwnerKey(account, role, origin) {
    const id = typeof account?.id === 'string' || typeof account?.id === 'number' ? String(account.id) : '';
    return id ? JSON.stringify([id, typeof account?.tenantId === 'string' ? account.tenantId : '', role || '', origin || '']) : null;
}
